import { useMemo, useState } from "react";
import { AlertTriangle, Mail, MailX, Search, Send, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import {
  publishSummaryMessage,
  useProceedingPanel,
  usePublishProceeding,
  type MailScope,
} from "@/features/rfq/api/proceedings";

/**
 * Ekran publikacji ogłoszenia — treść zamraża się na zawsze, a poczty nie da się cofnąć,
 * więc obie te rzeczy kupiec potwierdza świadomie w jednym miejscu.
 *
 * Sedno to wybór adresatów ZAPROSZENIA MAILOWEGO. Domyślna wysyłka do całego kręgu bywa
 * niepożądana (rozeznanie rynku, powtórka postępowania, jedna firma dopraszana imiennie),
 * a raz wysłanego maila nikt nie odwoła. Wybór dotyczy WYŁĄCZNIE poczty: kto ogłoszenie
 * widzi i może złożyć ofertę, wynika z trybu dostępu i nie zmienia się tutaj ani o krok.
 */

export function PublishDialog({
  proceedingId,
  onClose,
}: {
  proceedingId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useProceedingPanel(proceedingId);
  const publish = usePublishProceeding();
  const [scope, setScope] = useState<MailScope | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const proceeding = data?.proceeding;
  const audience = useMemo(() => data?.audience ?? [], [data]);
  const isOpenMode = proceeding?.access_mode === "open";
  // Tryb otwarty nigdy nie szedł pocztą do całej bazy i nadal nie idzie — domyślny wybór
  // odwzorowuje dotychczasowe zachowanie obu trybów, żeby „po prostu opublikuj" nic nie zmieniło.
  const effectiveScope: MailScope = scope ?? (isOpenMode ? "none" : "all");

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle
      ? audience.filter((row) => row.name.toLowerCase().includes(needle))
      : audience;
  }, [audience, search]);

  if (isLoading || !proceeding) {
    return (
      <Modal open onClose={onClose} title="Publikacja ogłoszenia" size="lg">
        <PageLoader />
      </Modal>
    );
  }

  const toggle = (id: string) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = () => {
    setError(null);
    publish.mutate(
      {
        id: proceedingId,
        mail_scope: effectiveScope,
        mail_contractors: effectiveScope === "selected" ? picked : [],
      },
      {
        onSuccess: (meta) => {
          alert(publishSummaryMessage(meta));
          onClose();
        },
        onError: (err: unknown) => {
          const detail = (
            err as { response?: { data?: { errors?: { detail?: string } } } }
          ).response?.data?.errors?.detail;
          setError(detail ?? "Nie udało się opublikować.");
        },
      },
    );
  };

  const nothingToMail = effectiveScope === "selected" && picked.length === 0;

  return (
    <Modal open onClose={onClose} title={`Publikacja ${proceeding.number}`} size="lg">
      <div className="space-y-5 text-sm">
        <div className="flex items-start gap-2 border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
          <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-[var(--color-text-secondary)]">
            Po publikacji treści ogłoszenia nie da się już zmienić — oferenci składają
            oferty na konkretne warunki.
          </p>
        </div>

        <div>
          <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <Users size={12} /> Krąg odbiorców ({audience.length})
          </p>
          <p className="mt-1 text-[var(--color-text-secondary)]">
            Ogłoszenie zobaczy {audience.length} firm(y) — decyduje o tym tryb dostępu
            („{proceeding.access_mode_label}") i to się poniżej nie zmienia. Każde konto
            z tego kręgu dostaje powiadomienie w systemie.
          </p>
        </div>

        <div>
          <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <Mail size={12} /> Zaproszenie mailem
          </p>
          <div className="space-y-2">
            <Choice
              checked={effectiveScope === "all"}
              onSelect={() => setScope("all")}
              disabled={isOpenMode}
              label={`Do wszystkich firm z kręgu (${audience.length})`}
              hint={
                isOpenMode
                  ? "Niedostępne dla ogłoszenia otwartego — to ogłoszenie, nie zaproszenie imienne do całej bazy firm."
                  : "Zachowanie dotychczasowe."
              }
            />
            <Choice
              checked={effectiveScope === "selected"}
              onSelect={() => setScope("selected")}
              label="Tylko do wskazanych firm"
              hint="Reszta kręgu zobaczy ogłoszenie w systemie, ale nie dostanie maila."
            />
            <Choice
              checked={effectiveScope === "none"}
              onSelect={() => setScope("none")}
              label="Bez maili"
              hint="Samo ogłoszenie i powiadomienie w systemie — żadna firma nie dostanie poczty."
            />
          </div>
        </div>

        {effectiveScope === "selected" && (
          <div>
            <div className="relative mb-2">
              <Search
                size={13}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj firmy…"
                className="w-full border border-[var(--color-border)] bg-transparent py-1.5 pl-8 pr-3 text-sm text-[var(--color-text-primary)]"
              />
            </div>
            {audience.length === 0 ? (
              <p className="text-[var(--color-text-muted)]">
                Krąg odbiorców jest pusty — nie ma kogo zawiadomić.
              </p>
            ) : (
              <>
                <div className="max-h-56 overflow-y-auto border border-[var(--color-border)]">
                  {visible.map((row) => (
                    <label
                      key={row.id}
                      className="flex cursor-pointer items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 last:border-0 hover:bg-[var(--color-bg-subtle)]"
                    >
                      <input
                        type="checkbox"
                        checked={picked.includes(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                      <span className="text-[var(--color-text-primary)]">{row.name}</span>
                    </label>
                  ))}
                  {visible.length === 0 && (
                    <p className="px-3 py-2 text-[var(--color-text-muted)]">
                      Żadna firma z kręgu nie pasuje do wyszukiwania.
                    </p>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => setPicked(audience.map((row) => row.id))}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    Zaznacz wszystkie
                  </button>
                  <button
                    type="button"
                    onClick={() => setPicked([])}
                    className="text-[var(--color-text-secondary)] hover:underline"
                  >
                    Odznacz wszystkie
                  </button>
                  <span className="text-[var(--color-text-muted)]">
                    Wybrano {picked.length} z {audience.length}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {effectiveScope === "none" && (
          <p className="flex items-start gap-2 text-[var(--color-text-secondary)]">
            <MailX size={15} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
            Żadna firma nie dostanie maila. Ogłoszenie i tak pojawi się na liście
            postępowań u wszystkich z kręgu odbiorców.
          </p>
        )}

        {error && <p className="text-red-600">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--color-border)] pt-4">
          <Button variant="secondary" onClick={onClose} disabled={publish.isPending}>
            Anuluj
          </Button>
          <Button onClick={submit} disabled={publish.isPending || nothingToMail}>
            <Send size={14} className="mr-1.5 inline" />
            {publish.isPending ? "Publikowanie…" : "Opublikuj"}
          </Button>
        </div>
        {nothingToMail && (
          <p className="text-right text-xs text-[var(--color-text-muted)]">
            Wskaż firmy do zawiadomienia albo wybierz „Bez maili".
          </p>
        )}
      </div>
    </Modal>
  );
}

function Choice({
  checked,
  onSelect,
  label,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-2 border p-2.5 ${
        checked
          ? "border-[var(--color-primary)] bg-[var(--color-bg-subtle)]"
          : "border-[var(--color-border)]"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <input
        type="radio"
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span>
        <span className="block text-[var(--color-text-primary)]">{label}</span>
        <span className="block text-xs text-[var(--color-text-muted)]">{hint}</span>
      </span>
    </label>
  );
}
